import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

interface Props {
  children: React.ReactNode;
  label?: string;
}
interface State { err: Error | null; retryKey: number; }

/** Per-pane error boundary. Prevents a rendering failure (e.g. lightweight-charts
 *  primitive throwing during extreme zoom) from white-screening the whole page. */
export default class ChartErrorBoundary extends React.Component<Props, State> {
  state: State = { err: null, retryKey: 0 };

  static getDerivedStateFromError(err: Error): Partial<State> { return { err }; }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ChartErrorBoundary]', this.props.label || '', err, info);
  }

  handleRetry = () => {
    this.setState(s => ({ err: null, retryKey: s.retryKey + 1 }));
  };

  render() {
    if (this.state.err) {
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', width: '100%', height: '100%',
          minHeight: 240, gap: 1, p: 2, border: '1px dashed', borderColor: 'divider',
          borderRadius: 1, bgcolor: 'background.paper',
        }}>
          <Typography variant="subtitle2" color="error">Chart failed to render</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360, textAlign: 'center' }}>
            {this.state.err.message || 'Unknown rendering error'}
          </Typography>
          <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={this.handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
